import { useState, type FormEvent } from 'react';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { Button, ErrorState } from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { useSessionScopeFence } from '@/hooks/useSessionScope';
import { useAiGuardrails, useAiGuardrailMutations } from './aiSettings.hooks';
import type { AiGuardrailPolicy, AiGuardrailRule } from './aiSettings.types';
import { SettingsDraftActions } from './SettingsDraftActions';
import {
  SettingsField,
  SettingsLoadingState,
  SettingsMutationBanner,
  SettingsSection,
  SettingsToggle,
  settingsControlClass,
} from './settingsChrome';
import { changedSettingsFields, useSettingsDraft } from './useSettingsDraft';

const policyLabels: Record<keyof AiGuardrailPolicy, string> = {
  operatingInstructions: 'Operating instructions',
  financialSafety: 'Financial safety',
  dataPrivacy: 'Data privacy',
  escalationPolicy: 'Escalation policy',
  evidenceStandards: 'Evidence standards',
};
const policyKeys = Object.keys(policyLabels) as (keyof AiGuardrailPolicy)[];
const ruleLabels = {
  restriction: 'Restriction',
  permission: 'Permission',
  tone: 'Tone / style',
  knowledge: 'Knowledge scope',
};
const emptyRule = { type: 'restriction' as AiGuardrailRule['type'], description: '', active: true };
type RuleForm = Pick<AiGuardrailRule, 'type' | 'description' | 'active'>;

export function AiGuardrailSettings() {
  const query = useAiGuardrails();
  const mutations = useAiGuardrailMutations();
  const auth = useAuth();
  const fence = useSessionScopeFence(PERMISSIONS.SETTINGS_MANAGE);
  const draft = useSettingsDraft(
    query.data ? { form: query.data.policy, version: query.data.version } : null,
  );
  const [notice, setNotice] = useState<{ scope: string; message: string; error?: Error } | null>(
    null,
  );
  const canManage = Boolean(
    query.data?.canManage &&
      fence.enabled &&
      hasPermission(auth.permissions, PERMISSIONS.SETTINGS_MANAGE),
  );
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (
      !canManage ||
      mutations.updatePolicy.isPending ||
      !draft.form ||
      !draft.baseline ||
      draft.version === null ||
      !draft.dirty
    )
      return;
    const origin = fence.identity;
    try {
      const saved = await mutations.updatePolicy.mutateAsync({
        expectedVersion: draft.version,
        policy: changedSettingsFields(draft.form, draft.baseline, policyKeys),
      });
      if (!fence.isCurrent(origin)) return;
      draft.accept({ form: saved.policy, version: saved.version });
      setNotice({ scope: draft.scope, message: 'Guardrail policy saved.' });
    } catch (error) {
      if (fence.isCurrent(origin))
        setNotice({
          scope: draft.scope,
          message: '',
          error: error instanceof Error ? error : new Error('Guardrail policy could not be saved.'),
        });
    }
  };
  if (query.isLoading) return <SettingsLoadingState label="Loading AI guardrails…" />;
  if (!query.data)
    return (
      <ErrorState
        title="AI guardrails unavailable"
        description="Workspace policy could not be loaded."
        action={
          <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
            Retry guardrails
          </Button>
        }
      />
    );
  return (
    <div className="grid gap-2.5">
      <SettingsSection
        title="Workspace guardrail policy"
        description="Additional instructions supplied to assistants. Required approval and access checks remain enforced."
        actions={
          <>
            <span className="text-2xs text-muted-foreground">Version {query.data.version}</span>
            <Button
              size="xs"
              variant="outline"
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              Refresh guardrails
            </Button>
          </>
        }
      >
        <form onSubmit={(event) => void submit(event)} className="grid gap-2.5">
          {query.isError ? (
            <p className="text-warning text-xs" role="status">
              Refresh failed. Showing the last loaded policy.
            </p>
          ) : null}
          <p className="border-border bg-background/40 text-muted-foreground rounded-lg border px-2.5 py-2 text-xs">
            {query.data.enforcedPolicy}
          </p>
          <SettingsMutationBanner
            message={notice?.scope === draft.scope ? notice.message : null}
            error={notice?.scope === draft.scope ? notice.error : null}
          />
          <SettingsDraftActions
            dirty={draft.dirty}
            changedOnServer={draft.changedOnServer}
            onRevert={() => {
              draft.accept();
              setNotice(null);
            }}
            disabled={mutations.updatePolicy.isPending}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            {policyKeys.map((key) => (
              <SettingsField key={key} label={policyLabels[key]}>
                <textarea
                  className={`${settingsControlClass} min-h-20 resize-y rounded-lg py-2`}
                  value={draft.form?.[key] ?? ''}
                  required
                  minLength={10}
                  maxLength={4_000}
                  readOnly={!canManage}
                  disabled={mutations.updatePolicy.isPending}
                  onChange={(event) =>
                    draft.form && draft.change({ ...draft.form, [key]: event.target.value })
                  }
                />
              </SettingsField>
            ))}
          </div>
          {canManage ? (
            <div>
              <Button
                size="xs"
                type="submit"
                disabled={mutations.updatePolicy.isPending || !draft.dirty}
              >
                {mutations.updatePolicy.isPending ? 'Saving…' : 'Save guardrail policy'}
              </Button>
            </div>
          ) : (
            <p className="text-muted-foreground text-xs">
              Workspace settings permission is required to edit policy.
            </p>
          )}
        </form>
      </SettingsSection>
      <SettingsSection
        title="Custom guardrail rules"
        description="Typed rules add restrictions, permissions, tone, or knowledge guidance within platform safeguards."
        actions={
          <span className="text-2xs text-muted-foreground">
            {query.data.rules.filter((rule) => rule.active).length} active ·{' '}
            {query.data.rules.length}/50 rules
          </span>
        }
      >
        <div className="grid gap-2.5">
          {query.data.rules.map((rule) => (
            <GuardrailRuleEditor
              key={`${draft.scope}:${rule.id}`}
              rule={rule}
              version={query.data!.version}
              canManage={canManage}
            />
          ))}
          {query.data.rules.length === 0 ? (
            <p className="text-muted-foreground text-xs">
              No custom rules yet. Workspace policy still applies.
            </p>
          ) : null}
          {canManage ? (
            <GuardrailRuleEditor
              key={`${draft.scope}:new`}
              version={query.data.version}
              canManage={canManage}
              atLimit={query.data.rules.length >= 50}
            />
          ) : null}
        </div>
      </SettingsSection>
    </div>
  );
}

function GuardrailRuleEditor({
  rule,
  version,
  canManage,
  atLimit = false,
}: {
  rule?: AiGuardrailRule;
  version: number;
  canManage: boolean;
  atLimit?: boolean;
}) {
  const mutations = useAiGuardrailMutations();
  const fence = useSessionScopeFence(PERMISSIONS.SETTINGS_MANAGE);
  const draft = useSettingsDraft<RuleForm>({
    form: rule
      ? { type: rule.type, description: rule.description, active: rule.active }
      : emptyRule,
    version,
  });
  const [notice, setNotice] = useState<{ scope: string; message: string; error?: Error } | null>(
    null,
  );
  const busy =
    mutations.createRule.isPending ||
    mutations.updateRule.isPending ||
    mutations.removeRule.isPending;
  const captureError = (error: unknown) =>
    setNotice({
      scope: draft.scope,
      message: '',
      error: error instanceof Error ? error : new Error('Guardrail rule could not be changed.'),
    });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (
      !canManage ||
      !fence.enabled ||
      busy ||
      !draft.form ||
      !draft.baseline ||
      draft.version === null ||
      !draft.dirty ||
      (!rule && atLimit)
    )
      return;
    const origin = fence.identity;
    try {
      const saved = rule
        ? await mutations.updateRule.mutateAsync({
            id: rule.id,
            expectedVersion: draft.version,
            ...changedSettingsFields(draft.form, draft.baseline, ['type', 'description', 'active']),
          })
        : await mutations.createRule.mutateAsync({ expectedVersion: draft.version, ...draft.form });
      if (!fence.isCurrent(origin)) return;
      const savedRule = rule ? saved.rules.find((item) => item.id === rule.id) : null;
      draft.accept({
        form: savedRule
          ? { type: savedRule.type, description: savedRule.description, active: savedRule.active }
          : emptyRule,
        version: saved.version,
      });
      setNotice({
        scope: draft.scope,
        message: rule ? 'Guardrail rule saved.' : 'Guardrail rule added.',
      });
    } catch (error) {
      if (fence.isCurrent(origin)) captureError(error);
    }
  };
  const remove = async () => {
    if (!canManage || !fence.enabled || busy || !rule) return;
    const origin = fence.identity;
    try {
      await mutations.removeRule.mutateAsync({ id: rule.id, expectedVersion: version });
    } catch (error) {
      if (fence.isCurrent(origin)) captureError(error);
    }
  };
  if (!draft.form) return null;
  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="border-border grid gap-2 border-t pt-2.5"
      aria-label={rule ? `Guardrail rule: ${rule.description}` : 'Add guardrail rule'}
    >
      <SettingsMutationBanner
        message={notice?.scope === draft.scope ? notice.message : null}
        error={notice?.scope === draft.scope ? notice.error : null}
      />
      <SettingsDraftActions
        dirty={draft.dirty}
        changedOnServer={draft.changedOnServer}
        onRevert={() => {
          draft.accept();
          setNotice(null);
        }}
        disabled={busy}
      />
      <div className="grid gap-2 sm:grid-cols-[180px_1fr]">
        <SettingsField label="Rule type">
          <select
            className={settingsControlClass}
            value={draft.form.type}
            disabled={!canManage || busy}
            onChange={(event) =>
              draft.change({ ...draft.form!, type: event.target.value as AiGuardrailRule['type'] })
            }
          >
            {Object.entries(ruleLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </SettingsField>
        <SettingsField label="Rule description">
          <textarea
            className={`${settingsControlClass} min-h-16 resize-y rounded-lg py-2`}
            value={draft.form.description}
            required
            minLength={10}
            maxLength={1_000}
            readOnly={!canManage}
            disabled={busy}
            onChange={(event) => draft.change({ ...draft.form!, description: event.target.value })}
          />
        </SettingsField>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SettingsToggle
          label="Rule active"
          checked={draft.form.active}
          disabled={!canManage || busy}
          onChange={(active) => draft.change({ ...draft.form!, active })}
        />
        {canManage ? (
          <>
            <Button size="xs" type="submit" disabled={busy || !draft.dirty || (!rule && atLimit)}>
              {rule ? 'Save rule' : 'Add rule'}
            </Button>
            {rule ? (
              <Button
                size="xs"
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => void remove()}
              >
                Delete rule
              </Button>
            ) : null}
          </>
        ) : null}
        {atLimit ? (
          <span className="text-warning text-2xs">
            The workspace has reached its 50-rule limit.
          </span>
        ) : null}
      </div>
    </form>
  );
}
